export const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const isExpired = (decoded) => {
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now();
};

export const getKeycloakTokens = () => {
  const accessToken = sessionStorage.getItem("access_token");
  const refreshToken = sessionStorage.getItem("refresh_token");
  const idToken = sessionStorage.getItem("id_token");
  return { accessToken, refreshToken, idToken };
};

export const clearAllAuth = () => {
  try {
    sessionStorage.clear();
    localStorage.clear();
  } catch {}
};

export const refreshKeycloakTokens = async () => {
  const { refreshToken } = getKeycloakTokens();
  if (!refreshToken) return false;

  const params = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: process.env.REACT_APP_KC_CLIENT_ID || "kt-admin-app",
    refresh_token: refreshToken,
  });
  const clientSecret = process.env.REACT_APP_KC_CLIENT_SECRET;
  if (clientSecret) params.append("client_secret", clientSecret);

  const realmBase = process.env.REACT_APP_KC_REALM_BASE || "https://meotrikapm.com/sso/realms/DEV-REALM";
  const tokenEndpoint = `${realmBase}/protocol/openid-connect/token`;
  const resp = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });
  if (!resp.ok) return false;
  const t = await resp.json();
  if (t.id_token) sessionStorage.setItem("id_token", t.id_token);
  if (t.access_token) sessionStorage.setItem("access_token", t.access_token);
  if (t.refresh_token) sessionStorage.setItem("refresh_token", t.refresh_token);
  if (t.expires_in) sessionStorage.setItem("token_expires_at", (Date.now() + t.expires_in * 1000).toString());
  if (t.session_state) sessionStorage.setItem("kc_session_state", t.session_state);
  try {
    if (t.id_token) {
      const decoded = decodeJwt(t.id_token);
      if (decoded?.sid) sessionStorage.setItem("kc_sid", decoded.sid);
    }
  } catch {}
  return true;
};

export const getIsAuthenticated = async () => {
  // Prefer Keycloak tokens
  const { accessToken } = getKeycloakTokens();
  if (accessToken) {
    const decoded = decodeJwt(accessToken);
    if (!isExpired(decoded)) return true;
    // try refresh
    const refreshed = await refreshKeycloakTokens();
    if (refreshed) return true;
    return false;
  }
  // Fallback to legacy token if present (no expiry check, legacy server validates)
  const legacy = localStorage.getItem("token");
  if (legacy) return true;
  return false;
};


export const logoutFromKeycloakAndClear = async () => {
  try {
    const realmBase = process.env.REACT_APP_KC_REALM_BASE || "https://meotrikapm.com/sso/realms/DEV-REALM";
    const clientId = process.env.REACT_APP_KC_CLIENT_ID || "kt-admin-app";
    const clientSecret = process.env.REACT_APP_KC_CLIENT_SECRET;
    const postLogout = process.env.REACT_APP_OIDC_LOGOUT_REDIRECT_URI;

    const idToken = sessionStorage.getItem("id_token") || localStorage.getItem("id_token");
    const refreshToken = sessionStorage.getItem("refresh_token") || localStorage.getItem("refresh_token");
    const accessToken = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");

    // 1) Revoke tokens (best-effort)
    if (realmBase && (refreshToken || accessToken)) {
      const revocationEndpoint = `${realmBase.replace(/\/$/, "")}/protocol/openid-connect/revoke`;
      const revoke = async (token, hint) => {
        if (!token) return;
        const params = new URLSearchParams({ client_id: clientId, token });
        if (clientSecret) params.append("client_secret", clientSecret);
        if (hint) params.append("token_type_hint", hint);
        try {
          await fetch(revocationEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params,
          });
        } catch {}
      };
      // Try revoking refresh first, then access
      await revoke(refreshToken, "refresh_token");
      await revoke(accessToken, "access_token");
    }

    // 2) Back-channel logout via POST with refresh_token (client session invalidation)
    if (realmBase && refreshToken) {
      const logoutEndpoint = `${realmBase.replace(/\/$/, "")}/protocol/openid-connect/logout`;
      const params = new URLSearchParams({ client_id: clientId, refresh_token: refreshToken });
      if (clientSecret) params.append("client_secret", clientSecret);
      try {
        await fetch(logoutEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        });
      } catch {}
    }

    // 3) Front-channel end-session (SSO session termination in browser)
    if (realmBase) {
      const endSession = `${realmBase.replace(/\/$/, "")}/protocol/openid-connect/logout`;
      const url = new URL(endSession);
      if (idToken) url.searchParams.set("id_token_hint", idToken);
      if (clientId) url.searchParams.set("client_id", clientId);
      url.searchParams.set("post_logout_redirect_uri", postLogout);
      clearAllAuth();
      window.location.assign(url.toString());
      return;
    }

    // 4) Last resort
    clearAllAuth();
    window.location.assign(postLogout);
  } catch {
    clearAllAuth();
    try { window.location.assign("/"); } catch {}
  }
};


